/**
 * Shared checks for the "Funciones" projects: did the student get the right
 * value AND use the function the task is about?
 *
 * `range.formulas` always comes back in English with "," separators, whatever
 * the language of the user's Excel, but newer functions may carry internal
 * prefixes (`_xlfn.XLOOKUP`, `_xlfn._xlws.FILTER`) that we strip.
 */

export interface CheckResult {
  passed: boolean;
  message?: string;
}

/** English name → Spanish name, for detection and for messages. */
const SPANISH_NAMES: Record<string, string> = {
  XLOOKUP: "BUSCARX",
  SUMIFS: "SUMAR.SI.CONJUNTO",
  FILTER: "FILTRAR",
  SORT: "ORDENAR",
  SORTBY: "ORDENARPOR",
  IFERROR: "SI.ERROR",
  COUNTA: "CONTARA",
  ROWS: "FILAS",
  SUM: "SUMA",
  MAX: "MAX",
  ISNUMBER: "ESNUMERO",
  SEARCH: "HALLAR",
  CHOOSECOLS: "ELEGIRCOLS",
  CHOOSE: "ELEGIR",
  IF: "SI",
  IFS: "SI.CONJUNTO",
  AND: "Y",
  OR: "O",
  NOT: "NO",
  IFNA: "SI.ND",
  VLOOKUP: "BUSCARV",
  ISBLANK: "ESBLANCO",
  ISTEXT: "ESTEXTO",
  ISERROR: "ESERROR",
  COUNTIFS: "CONTAR.SI.CONJUNTO",
  AVERAGEIFS: "PROMEDIO.SI.CONJUNTO",
  MAXIFS: "MAX.SI.CONJUNTO",
  MINIFS: "MIN.SI.CONJUNTO",
  INDEX: "INDICE",
  UNIQUE: "UNICOS",
  XMATCH: "COINCIDIRX",
  SEQUENCE: "SECUENCIA",
  LARGE: "K.ESIMO.MAYOR",
  DATE: "FECHA",
  YEAR: "AÑO",
  MONTH: "MES",
  WEEKDAY: "DIASEM",
  LEFT: "IZQUIERDA",
  RIGHT: "DERECHA",
  MID: "EXTRAE",
  LEN: "LARGO",
  VALUE: "VALOR",
  TRIM: "ESPACIOS",
  PROPER: "NOMPROPIO",
  LOWER: "MINUSC",
  UPPER: "MAYUSC",
  SUBSTITUTE: "SUSTITUIR",
  CHAR: "CARACTER",
  TEXTBEFORE: "TEXTOANTES",
  TEXTAFTER: "TEXTODESPUES",
  TEXTSPLIT: "DIVIDIRTEXTO",
  TEXTJOIN: "UNIRCADENAS",
  COLUMNS: "COLUMNAS",
  DAY: "DIA",
  DAYS: "DIAS",
  EDATE: "FECHA.MES",
  EOMONTH: "FIN.MES",
  DATEDIF: "SIFECHA",
  YEARFRAC: "FRAC.AÑO",
  NETWORKDAYS: "DIAS.LAB",
  WORKDAY: "DIA.LAB",
  "NETWORKDAYS.INTL": "DIAS.LAB.INTL",
  "WORKDAY.INTL": "DIA.LAB.INTL",
  TIME: "NSHORA",
  MOD: "RESIDUO",
  ROUND: "REDONDEAR",
  ROUNDUP: "REDONDEAR.MAS",
  MIN: "MIN",
  SUMPRODUCT: "SUMAPRODUCTO",
  AVERAGE: "PROMEDIO",
  MEDIAN: "MEDIANA",
  "MODE.SNGL": "MODA.UNO",
  "STDEV.S": "DESVEST.M",
  "PERCENTILE.INC": "PERCENTIL.INC",
  "QUARTILE.INC": "CUARTIL.INC",
  "RANK.EQ": "JERARQUIA.EQV",
  "RANK.AVG": "JERARQUIA.MEDIA",
  SMALL: "K.ESIMO.MENOR",
  INT: "ENTERO",
  MROUND: "REDOND.MULT",
  ROUNDDOWN: "REDONDEAR.MENOS",
  CHOOSEROWS: "ELEGIRFILAS",
  TAKE: "TOMAR",
  DROP: "EXCLUIR",
  VSTACK: "APILARV",
  HSTACK: "APILARH",
  TOCOL: "ENCOL",
  TOROW: "ENFILA",
  WRAPROWS: "AJUSTARFILAS",
  WRAPCOLS: "AJUSTARCOLS",
  EXPAND: "EXPANDIR",
  LET: "LET",
  SUMIF: "SUMAR.SI",
  COUNTIF: "CONTAR.SI",
  MATCH: "COINCIDIR",
};

/**
 * `fn` may list alternatives separated by "|" ("VLOOKUP|XLOOKUP"): any one
 * of them satisfies the requirement.
 */
export function spanishName(fn: string): string {
  return fn
    .split("|")
    .map((name) => SPANISH_NAMES[name] ?? name)
    .join(" o ");
}

export function normalizeFormula(formula: unknown): string {
  return String(formula ?? "")
    .replace(/^=/, "")
    .replace(/\$/g, "")
    .replace(/\s+/g, "")
    .toUpperCase()
    .replace(/_XLFN\.|_XLWS\.|_XLPM\./g, "");
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** True when the formula calls `fn` (English or Spanish name). */
export function usesFunction(formula: unknown, fn: string): boolean {
  const normalized = normalizeFormula(formula);
  const names = [fn.toUpperCase(), spanishName(fn).toUpperCase()];
  return names.some((name) =>
    // Not preceded by a letter, digit, dot or underscore, so SUM( does not
    // match inside SUMIFS( and CHOOSE( does not match CHOOSECOLS(.
    new RegExp(`(^|[^A-Z0-9._])${escapeRegExp(name)}\\(`).test(normalized)
  );
}

/** How many times the formula calls `fn` (e.g. nested BUSCARX needs 2). */
export function countFunction(formula: unknown, fn: string): number {
  const normalized = normalizeFormula(formula);
  const names = [fn.toUpperCase(), spanishName(fn).toUpperCase()];
  return names.reduce(
    (total, name) =>
      total +
      (normalized.match(
        new RegExp(`(^|[^A-Z0-9._])${escapeRegExp(name)}\\(`, "g")
      )?.length ?? 0),
    0
  );
}

export function usesAnyFunction(formula: unknown, fns: string[]): boolean {
  return fns.some((fn) => usesFunction(formula, fn));
}

function isNumberLike(value: unknown): boolean {
  if (typeof value === "number") return true;
  if (typeof value !== "string" || value.trim() === "") return false;
  return !Number.isNaN(Number(value));
}

/**
 * `exact` compares text character by character (case and spaces count),
 * which is what text-cleaning tasks grade. Numbers always use a tolerance.
 */
export function sameValue(actual: unknown, expected: unknown, exact = false): boolean {
  if (exact && typeof expected === "string" && !isNumberLike(expected)) {
    return String(actual ?? "") === expected;
  }
  if (isNumberLike(expected) && isNumberLike(actual)) {
    return Math.abs(Number(actual) - Number(expected)) < 1e-6;
  }
  return (
    String(actual ?? "").trim().toLowerCase() ===
    String(expected ?? "").trim().toLowerCase()
  );
}

export function sameMatrix(actual: unknown[][], expected: unknown[][], exact = false): boolean {
  if (actual.length !== expected.length) return false;
  for (let r = 0; r < expected.length; r++) {
    if (actual[r].length !== expected[r].length) return false;
    for (let c = 0; c < expected[r].length; c++) {
      if (!sameValue(actual[r][c], expected[r][c], exact)) return false;
    }
  }
  return true;
}

const fail = (message: string): CheckResult => ({ passed: false, message });

export const MESSAGES = {
  empty: (cell: string) => `La celda ${cell} está vacía.`,
  functionUnavailable: (fn: string) =>
    `Tu versión de Excel no reconoce ${spanishName(fn)}. Necesitas Microsoft 365 o Excel 2021+.`,
  nameError:
    "Excel no reconoce un nombre en tu fórmula. Revisa cómo escribiste la función o el rango.",
  spillBlocked: (cell: string) =>
    `El resultado no puede derramarse: libera las celdas debajo/derecha de ${cell}.`,
  missingFunction: (fn: string) =>
    `Usa la función ${spanishName(fn)} para resolver esta tarea.`,
  wrongValue: "El resultado no coincide con lo esperado.",
};

/**
 * Steps 1–4 of the evaluation order shared by every check: empty cell,
 * #NAME?, #SPILL!, missing function. Returns null when the formula is fine
 * to compare by value.
 */
function precheck(
  cell: string,
  formula: unknown,
  value: unknown,
  valueType: string,
  fns: string[]
): CheckResult | null {
  const raw = String(formula ?? "").trim();
  if (raw === "" && String(value ?? "").trim() === "") {
    return fail(MESSAGES.empty(cell));
  }

  if (valueType === Excel.RangeValueType.error) {
    const text = String(value).toUpperCase();
    if (text.includes("NAME") || text.includes("NOMBRE")) {
      const target = fns
        .flatMap((fn) => fn.split("|"))
        .find((fn) => usesFunction(formula, fn));
      return fail(target ? MESSAGES.functionUnavailable(target) : MESSAGES.nameError);
    }
    if (text.includes("SPILL") || text.includes("DESBORD")) {
      return fail(MESSAGES.spillBlocked(cell));
    }
  }

  const missing = fns.find(
    (fn) => !fn.split("|").some((alt) => usesFunction(formula, alt))
  );
  if (missing) return fail(MESSAGES.missingFunction(missing));

  return null;
}

/**
 * Accepts a list of functions that must ALL appear. For "any of" choices
 * (CHOOSECOLS or CHOOSE) check them in the project module.
 */
export async function checkCell(
  context: Excel.RequestContext,
  sheet: string,
  address: string,
  fns: string | string[],
  expected: unknown,
  exact = false
): Promise<CheckResult> {
  const required = Array.isArray(fns) ? fns : [fns];
  const cell = context.workbook.worksheets.getItem(sheet).getRange(address);
  cell.load("formulas,values,valueTypes");
  await context.sync();

  const pre = precheck(
    address,
    cell.formulas[0][0],
    cell.values[0][0],
    cell.valueTypes[0][0],
    required
  );
  if (pre) return pre;

  return sameValue(cell.values[0][0], expected, exact)
    ? { passed: true }
    : fail(MESSAGES.wrongValue);
}

/**
 * Checks a dynamic-array result starting at `address`. A formula that
 * returns a single value does not spill, so the anchor alone is compared.
 */
export async function checkSpill(
  context: Excel.RequestContext,
  sheet: string,
  address: string,
  fns: string | string[],
  expected: unknown[][],
  exact = false
): Promise<CheckResult> {
  const required = Array.isArray(fns) ? fns : [fns];
  const anchor = context.workbook.worksheets.getItem(sheet).getRange(address);
  anchor.load("formulas,values,valueTypes");
  const spill = anchor.getSpillingToRangeOrNullObject();
  spill.load("values");
  await context.sync();

  const pre = precheck(
    address,
    anchor.formulas[0][0],
    anchor.values[0][0],
    anchor.valueTypes[0][0],
    required
  );
  if (pre) return pre;

  const actual = spill.isNullObject ? anchor.values : spill.values;
  return sameMatrix(actual, expected, exact)
    ? { passed: true }
    : fail(MESSAGES.wrongValue);
}

/**
 * Checks a block filled by dragging a formula (e.g. a two-way summary):
 * every cell must use the function and match.
 */
export async function checkBlock(
  context: Excel.RequestContext,
  sheet: string,
  address: string,
  fns: string | string[],
  expected: unknown[][],
  exact = false
): Promise<CheckResult> {
  const required = Array.isArray(fns) ? fns : [fns];
  const range = context.workbook.worksheets.getItem(sheet).getRange(address);
  range.load("formulas,values,valueTypes,address");
  await context.sync();

  for (let r = 0; r < expected.length; r++) {
    for (let c = 0; c < expected[r].length; c++) {
      const pre = precheck(
        address,
        range.formulas[r][c],
        range.values[r][c],
        range.valueTypes[r][c],
        required
      );
      if (pre) return pre;
    }
  }

  return sameMatrix(range.values, expected, exact)
    ? { passed: true }
    : fail(MESSAGES.wrongValue);
}
